import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useRef, useState, useCallback } from 'react';
import { C } from '@parsina/framework/tokens';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button,
} from '@parsina/framework';

/**
 * Pan/zoom avatar cropper. Adapted from the clinic app's Settings screen so the
 * two apps frame profile pictures identically; the styling here is token-based
 * rather than the clinic version's hard-coded dark palette.
 *
 * Output is a SQUARE JPEG data URI, not a circle. The dashed circle is a framing
 * guide showing what will stay visible once a round <Avatar> masks it. Clipping
 * to a circle here would be actively wrong: JPEG has no alpha channel, so the
 * transparent corners a circular clip leaves behind encode as solid black, and
 * any square presentation of the image shows black corners.
 *
 * Props:
 *   open, onOpenChange — Radix controlled state
 *   src                — data URI of the image the user picked
 *   onCrop(dataUri)    — called with the cropped JPEG; the dialog then closes
 *   busy               — disables confirm while an upload is in flight
 *
 * Optional, for non-avatar images (the public page's cover, gallery, …):
 *   aspect             — crop box width / height (default 1, the avatar square)
 *   outWidth           — stored width in px (default 300); height = outWidth / aspect
 *   shape              — 'circle' (default: the avatar framing guide) | 'rect'
 *   title              — dialog title (default: the avatar title)
 * The defaults reproduce the avatar cropper exactly.
 */

const CROP_D = 260; // crop circle diameter, in modal viewport px
const CONT_W = 420; // image-area width
const CONT_H = 360; // image-area height
const CROP_CX = CONT_W / 2;
const CROP_CY = CONT_H / 2;
const CROP_R = CROP_D / 2;

// Stored edge length. 300 keeps a 64px avatar crisp on 3× displays while landing
// around 20–25 KB at this quality.
const OUT = 300;
const QUALITY = 0.9;

// How far past "just covers the crop circle" the user may zoom in.
const MAX_ZOOM_FACTOR = 5;

export default function AvatarCropModal({
  open, onOpenChange, src, onCrop, busy = false,
  aspect = 1, outWidth = OUT, shape = 'circle', title,
}) {
  const { t } = useTranslation();
  // The crop box. The avatar square is CROP_D × CROP_D; a wider aspect keeps the
  // box inside the image area with a 20px margin on each side.
  const boxW = aspect >= 1 ? Math.min(CROP_D * aspect, CONT_W - 40) : CROP_D * aspect;
  const boxH = boxW / aspect;
  const boxL = CROP_CX - boxW / 2;
  const boxT = CROP_CY - boxH / 2;
  const outW = Math.round(outWidth);
  const outH = Math.round(outWidth / aspect);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const [nat, setNat] = useState(null); // natural { w, h } of the loaded image
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [dragging, setDragging] = useState(false);

  // Reset per image, so reopening with a different file doesn't inherit the
  // previous one's pan/zoom.
  const onImgLoad = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return;
    setNat({ w: nw, h: nh });
    // Smallest scale at which the image still covers the whole crop circle —
    // anything less would leave background showing inside the avatar.
    const ms = Math.max(boxW / nw, boxH / nh);
    setMinScale(ms);
    setScale(ms);
    setTx(0);
    setTy(0);
  }, [boxW, boxH]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    dragRef.current = { x: e.clientX - tx, y: e.clientY - ty };
  };
  const onPointerMove = (e) => {
    if (!dragging || !dragRef.current) return;
    setTx(e.clientX - dragRef.current.x);
    setTy(e.clientY - dragRef.current.y);
  };
  const onPointerUp = () => {
    setDragging(false);
    dragRef.current = null;
  };

  const onWheel = (e) => {
    e.preventDefault();
    setScale(s => clamp(s - e.deltaY * 0.001, minScale, minScale * MAX_ZOOM_FACTOR));
  };

  const handleConfirm = () => {
    if (!imgRef.current || !nat) return;
    const canvas = canvasRef.current;
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');

    // JPEG cannot store transparency, so anything not covered by the image would
    // otherwise come out black. White matches the avatar backgrounds in both apps.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outW, outH);

    // Translate the on-screen layout into canvas space. On screen the image is
    // centred at (CROP_CX + tx, CROP_CY + ty) and the crop box's top-left corner
    // sits at (CROP_CX - CROP_R, CROP_CY - CROP_R); the canvas is the crop box
    // scaled from CROP_D to OUT.
    const ratio = outW / boxW;
    const imgW = nat.w * scale * ratio;
    const imgH = nat.h * scale * ratio;
    const imgX = ((CROP_CX + tx) - boxL - (nat.w * scale) / 2) * ratio;
    const imgY = ((CROP_CY + ty) - boxT - (nat.h * scale) / 2) * ratio;

    ctx.drawImage(imgRef.current, imgX, imgY, imgW, imgH);
    onCrop(canvas.toDataURL('image/jpeg', QUALITY));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-none w-auto p-0 overflow-hidden"
        data-testid="avatar-crop-modal"
      >
        <DialogHeader style={{ padding: '18px 22px 14px', marginBottom: 0, borderBottom: `1px solid ${C.border}` }}>
          <DialogTitle>{title ?? t('settings.avatar.editTitle')}</DialogTitle>
        </DialogHeader>

        <div
          style={{
            position: 'relative', width: CONT_W, height: CONT_H,
            overflow: 'hidden', background: C.bg,
            cursor: dragging ? 'grabbing' : 'grab',
            userSelect: 'none', touchAction: 'none',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          onWheel={onWheel}
        >
          <img
            ref={imgRef}
            src={src}
            alt=""
            onLoad={onImgLoad}
            draggable={false}
            style={nat ? {
              position: 'absolute',
              width: nat.w * scale,
              height: nat.h * scale,
              left: CROP_CX + tx - (nat.w * scale) / 2,
              top: CROP_CY + ty - (nat.h * scale) / 2,
              pointerEvents: 'none',
            } : { position: 'absolute', opacity: 0, pointerEvents: 'none' }}
          />

          {/* Dimming everywhere except the crop circle. */}
          <svg
            viewBox={`0 0 ${CONT_W} ${CONT_H}`}
            style={{ position: 'absolute', inset: 0, width: CONT_W, height: CONT_H, pointerEvents: 'none' }}
            aria-hidden="true"
          >
            <defs>
              <mask id="avatarCropMask">
                <rect width={CONT_W} height={CONT_H} fill="white" />
                {shape === 'circle'
                  ? <circle cx={CROP_CX} cy={CROP_CY} r={CROP_R} fill="black" />
                  : <rect x={boxL} y={boxT} width={boxW} height={boxH} rx="8" fill="black" />}
              </mask>
            </defs>
            <rect width={CONT_W} height={CONT_H} fill="rgba(0,0,0,0.5)" mask="url(#avatarCropMask)" />
            {shape === 'circle' ? (
              <circle
                cx={CROP_CX} cy={CROP_CY} r={CROP_R}
                fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="1.5" strokeDasharray="6 4"
              />
            ) : (
              <rect
                x={boxL} y={boxT} width={boxW} height={boxH} rx="8"
                fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="1.5" strokeDasharray="6 4"
              />
            )}
          </svg>

          <div style={{
            position: 'absolute', bottom: 10, insetInline: 0, textAlign: 'center',
            fontSize: 11, color: 'rgba(255,255,255,0.8)', pointerEvents: 'none',
          }}>
            {t('settings.avatar.cropHint')}
          </div>
        </div>

        <div style={{ padding: '14px 22px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, color: C.textMuted, flexShrink: 0 }}>{t('settings.avatar.small')}</span>
          <input
            type="range"
            min={minScale}
            max={minScale * MAX_ZOOM_FACTOR}
            step={0.01}
            value={scale}
            onChange={e => setScale(parseFloat(e.target.value))}
            aria-label={t('settings.avatar.zoom')}
            style={{ flex: 1, accentColor: C.navy }}
          />
          <span style={{ fontSize: 11, color: C.textMuted, flexShrink: 0 }}>{t('settings.avatar.large')}</span>
        </div>

        <DialogFooter style={{ padding: '16px 22px 20px' }}>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            disabled={busy || !nat}
            data-testid="avatar-crop-confirm"
          >
            {busy ? t('common.state.saving') : t('common.actions.confirmAndSave')}
          </Button>
        </DialogFooter>

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </DialogContent>
    </Dialog>
  );
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(v, hi));
}
